import type { MouseEventHandler } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useField } from 'formik'
import { useIntl } from 'react-intl'

import type { PurchasingPhase } from '@b-stock/fe-core/bstock-next/helpers'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import { usePrecisionNumberFormat } from '@b-stock/fe-core/bstock-react'

import type { BidFormFormikValues } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { useBidFormContext } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { useOptionalListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { getAppliedThreshold } from '@/components/listings/search/ListingSearchProvider/categoryThreshold'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import {
  trackBidNowEvent,
  trackBuyNowEvent,
  trackRaiseBidEvent,
  trackSignInToBidEvent,
  trackSignInToBuyEvent,
  type AnalyticsTracker,
} from '../../helpers/listViewAnalytics'
import { useActionPanel } from '../ActionPanelProvider'
import { useAuctionActionPostloadQuery } from './AuctionActionCellPostload'
import { getMinBidAmount } from './getMinBidAmount'

const ns = 'Listings.ListView.actionCell.auction'

/** Which control the form resolves to. */
export type AuctionActionCta =
  | 'bidNow'
  | 'buyNow'
  | 'raiseBid'
  | 'signInToBid'
  | 'signInToBuy'

/**
 * Everything the auction form does that is not rendering it.
 *
 * The form has two presentations — the panel's, and the collapsed icon's inline
 * overlay — while the field binding, precision formatting, error tooltip,
 * postload trigger, CTA resolution and click handling are the same in both.
 * They live here so a new presentation is JSX rather than a second bid path.
 *
 * The guest prompt is the caller's: `onUnauthenticated` fires after the sign-in
 * event is tracked and before anything else happens.
 */
export const useAuctionActionForm = ({
  activePhase,
  onClickBuyNow,
  onUnauthenticated,
  searchListing,
}: {
  activePhase: PurchasingPhase | null
  onClickBuyNow: () => void
  onUnauthenticated: (reason: string) => void
  searchListing: CombinedListingSearchResult
}) => {
  const isLoggedIn = !!useAccountId()
  const { formatMessage } = useIntl()
  const { trackButtonClicked, ...analytics } = useAnalytics()
  const { surface } = useActionPanel()
  // Category composition threshold active on the search the bid came from;
  // null outside a provider or when no threshold applies.
  const threshold = getAppliedThreshold(
    useOptionalListingSearch()?.state.filters ?? {}
  )

  const tracker: AnalyticsTracker = useMemo(
    () => ({ trackButtonClicked }),
    [trackButtonClicked]
  )

  /**
   * The rest spread allocates a fresh object every render, so it cannot be a
   * dependency of the memo below without defeating it — on every visible row of
   * a virtualised grid. The handler only needs its value at click time, so read
   * it through a ref. `ActionPanelProvider` does the same for the same reason.
   */
  const analyticsRef = useRef(analytics)
  analyticsRef.current = analytics // oxlint-disable-line react/refs

  const { bidBasis, bidPrecision, bidStatus, currency } = searchListing

  const [{ value }, { error, touched }, { setTouched, setValue }] =
    useField<BidFormFormikValues['amount']>('amount')

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    currency,
    format: bidBasis === 'ABSOLUTE' ? 'currency' : 'percent',
    precision: bidPrecision,
    precisionStored: 1,
  })

  const inputWrapperRef = useRef<HTMLDivElement | null>(null)
  const { setErrorTooltip } = useBidFormContext()

  useEffect(() => {
    if (inputWrapperRef.current) {
      setErrorTooltip({ referenceElement: inputWrapperRef.current })
    }
    return () => setErrorTooltip(null)
  }, [setErrorTooltip])

  const [hasStartedTypingBid, setHasStartedTypingBid] = useState(false)
  // Start the postload request fetching as soon as the user starts putting
  // in a number, so they can get the bid confirmation modal faster
  useAuctionActionPostloadQuery({
    searchListing,
    enabled: hasStartedTypingBid || !!value,
  })

  const hasActiveBid =
    bidStatus === BidStatus.LOSING || bidStatus === BidStatus.WINNING

  const isBuyNowPhase = activePhase === 'BINO'
  const minBidAmount = getMinBidAmount(searchListing, activePhase)

  const placeholder = useMemo(() => {
    const amount = formatPrecisionNumber(minBidAmount)
    return isBuyNowPhase ? amount : `${amount}+`
  }, [formatPrecisionNumber, isBuyNowPhase, minBidAmount])

  const cta = useMemo<AuctionActionCta>(() => {
    if (!isLoggedIn) return isBuyNowPhase ? 'signInToBuy' : 'signInToBid'
    if (hasActiveBid) return 'raiseBid'
    return isBuyNowPhase ? 'buyNow' : 'bidNow'
  }, [hasActiveBid, isBuyNowPhase, isLoggedIn])

  // The same string both paths show, so the wording cannot drift between them.
  const unAuthReason = formatMessage({
    id: `${ns}.cta.${isBuyNowPhase ? 'buyNow' : 'bidNow'}`,
  })

  /**
   * Abandons a half-entered amount, so the next presentation of this row's form
   * starts at its minimum rather than restoring what was typed.
   *
   * Clears `touched` alongside the value: left set, the field reopens already
   * showing the below-minimum error against an empty input.
   *
   * Deliberately not Formik's `resetForm`: in the postload form the initial
   * value is the amount that opened the confirmation modal, so a reset would
   * restore that bid rather than clear it.
   *
   * Stable, because callers hang effects off it — a fresh identity per render
   * would re-register a window listener on every keystroke. `useField`'s helpers
   * are themselves memoized, so this holds.
   */
  const resetValue = useCallback(() => {
    void setValue('')
    void setTouched(false)
  }, [setTouched, setValue])

  const onClickAction = useMemo<MouseEventHandler<HTMLButtonElement>>(() => {
    return (e) => {
      if (!isLoggedIn) {
        if (isBuyNowPhase) {
          trackSignInToBuyEvent(tracker, analyticsRef.current, surface)
        } else {
          trackSignInToBidEvent(tracker, analyticsRef.current, surface)
        }
        onUnauthenticated(unAuthReason)
        e.preventDefault()
        return
      }

      // Track analytics based on button type
      if (isBuyNowPhase) {
        // Buy Now doesn't require form input, always track
        trackBuyNowEvent(tracker, analyticsRef.current, surface)
      } else {
        // For Bid Now and Raise Bid, only track if form has a valid value
        const hasValidValue = value && !error
        if (hasValidValue) {
          if (hasActiveBid) {
            trackRaiseBidEvent(
              tracker,
              analyticsRef.current,
              surface,
              threshold
            )
          } else {
            trackBidNowEvent(tracker, analyticsRef.current, surface, threshold)
          }
        }
      }

      // "Buy Now" does not use formik.
      if (isBuyNowPhase) {
        onClickBuyNow()
        e.preventDefault()
      }
      // "Bid Now" or "Raise Bid": validate, submit with Formik.
      // - Do not prevent default.
    }
  }, [
    error,
    hasActiveBid,
    isBuyNowPhase,
    isLoggedIn,
    onClickBuyNow,
    onUnauthenticated,
    surface,
    threshold,
    tracker,
    unAuthReason,
    value,
  ])

  return {
    bidBasis,
    bidPrecision,
    cta,
    currency,
    error,
    /** Positions the bid form's error tooltip; also the input's wrapper. */
    inputWrapperRef,
    /** Buy Now has no amount to enter — the panel layout omits the input. */
    isBuyNowPhase,
    onChangeValue: (next: number | null) => setValue(next ?? ''),
    onClickAction,
    onKeyDownValue: (key: string) => {
      if (/^\d$/.test(key)) setHasStartedTypingBid(true)
    },
    placeholder,
    resetValue,
    touched,
    unAuthReason,
    value,
  }
}
